// Citation generation utilities for APA 7th edition format

/**
 * Parse a full name into components (first, middle, last)
 * Assumes format: "FirstName MiddleName LastName" or "FirstName LastName"
 * @param {string} fullName - Full name string (e.g., "Abdul Had Ali")
 * @returns {object} - Object with firstName, middleName, lastName
 */
export const parseName = (fullName) => {
	if (!fullName || !fullName.trim()) {
		return { firstName: '', middleName: '', lastName: '' };
	}

	const nameParts = fullName
		.trim()
		.split(/\s+/)
		.filter((part) => part.length > 0);

	if (nameParts.length === 0) {
		return { firstName: '', middleName: '', lastName: '' };
	} else if (nameParts.length === 1) {
		return { firstName: nameParts[0], middleName: '', lastName: '' };
	} else if (nameParts.length === 2) {
		return { firstName: nameParts[0], middleName: '', lastName: nameParts[1] };
	} else {
		// Three or more parts: first is firstName, last is lastName, everything in between is middleName
		const firstName = nameParts[0];
		const lastName = nameParts[nameParts.length - 1];
		const middleName = nameParts.slice(1, -1).join(' ');
		return { firstName, middleName, lastName };
	}
};

/**
 * Format a single name for reference citation
 * Format: "LastName, F. M." (lastname, firstname initial, middle name initial)
 * @param {string} fullName - Full name string
 * @returns {string} - Formatted name
 */
export const formatNameForReference = (fullName) => {
	const { firstName, middleName, lastName } = parseName(fullName);

	if (!lastName && !firstName) return '';

	if (!lastName) return firstName; // If only first name exists

	let formatted = lastName;

	if (firstName) {
		formatted += `, ${firstName.charAt(0).toUpperCase()}.`;
	}

	if (middleName) {
		// Handle multiple middle names
		const middleInitials = middleName
			.split(/\s+/)
			.map((name) => name.charAt(0).toUpperCase() + '.')
			.join(' ');
		formatted += ` ${middleInitials}`;
	}

	return formatted;
};

/**
 * Format a single translator name for reference citation
 * Format: "F. M. LastName" (firstname initial, middle name initial, lastname full)
 * Example: "Abdul Bais Khan" → "A. B. Khan"
 * @param {string} fullName - Full name string
 * @returns {string} - Formatted translator name
 */
export const formatTranslatorForReference = (fullName) => {
	const { firstName, middleName, lastName } = parseName(fullName);

	if (!lastName && !firstName) return '';

	if (!lastName) return firstName; // If only first name exists

	let formatted = '';

	// Add first name initial
	if (firstName) {
		formatted = `${firstName.charAt(0).toUpperCase()}.`;
	}

	// Add middle name initials
	if (middleName) {
		const middleInitials = middleName
			.split(/\s+/)
			.map((name) => name.charAt(0).toUpperCase() + '.')
			.join(' ');
		formatted += ` ${middleInitials}`;
	}

	// Add last name (full)
	if (formatted) {
		formatted += ` ${lastName}`;
	} else {
		formatted = lastName;
	}

	return formatted;
};

/**
 * Format a single name for in-text citation
 * Format: "LastName" (lastname only)
 * @param {string} fullName - Full name string
 * @returns {string} - Just the last name
 */
export const formatNameForInText = (fullName) => {
	const { lastName, firstName } = parseName(fullName);
	return lastName || firstName || 'Author';
};

/**
 * Parse organization name to extract full name and abbreviation
 * Detects format: "Full Organization Name (ABBR)"
 * @param {string} orgName - Organization name string
 * @returns {object} - Object with fullName and abbreviation
 * @example parseOrganizationName("World Health Organization (WHO)")
 *          returns { fullName: "World Health Organization", abbreviation: "WHO", hasAbbreviation: true }
 */
export const parseOrganizationName = (orgName) => {
	if (!orgName || !orgName.trim()) {
		return { fullName: '', abbreviation: '', hasAbbreviation: false };
	}

	// Match pattern: "Full Name (ABBR)" where ABBR is in parentheses at the end
	const match = orgName.match(/^(.+?)\s*\(([^)]+)\)\s*$/);

	if (match) {
		const fullName = match[1].trim();
		const abbreviation = match[2].trim();
		return {
			fullName,
			abbreviation,
			hasAbbreviation: true
		};
	}

	// No abbreviation found, return the whole name
	return {
		fullName: orgName.trim(),
		abbreviation: '',
		hasAbbreviation: false
	};
};

/**
 * Format organization name for in-text citation
 * First use: "Full Organization Name (ABBR)"
 * Subsequent uses: "ABBR"
 * @param {string} orgName - Organization name string
 * @param {boolean} isFirstUse - Whether this is the first citation of this organization
 * @returns {string} - Formatted organization name
 */
export const formatOrganizationForInText = (orgName, isFirstUse = true) => {
	const { fullName, abbreviation, hasAbbreviation } = parseOrganizationName(orgName);

	if (!hasAbbreviation) {
		// No abbreviation, just return the full name
		return fullName || 'Organization';
	}

	if (isFirstUse) {
		// First use: show full name with abbreviation
		return `${fullName} (${abbreviation})`;
	} else {
		// Subsequent uses: show only abbreviation
		return abbreviation;
	}
};


/**
 * Format multiple authors for reference citation
 * Rules:
 * - 1 author: "LastName, F. M."
 * - 2 authors: "LastName1, F. M., & LastName2, F. M."
 * - 3+ authors: "LastName1, F. M., LastName2, F. M., & LastName3, F. M."
 * @param {Array} authors - Array of full names
 * @returns {string} - Formatted authors string
 */
export const formatAuthorsForReference = (authors) => {
	if (!authors || authors.length === 0 || !authors[0]) return '';

	const validAuthors = authors.filter((a) => a && a.trim());
	if (validAuthors.length === 0) return '';

	const formattedAuthors = validAuthors.map((author) => formatNameForReference(author));

	if (formattedAuthors.length === 1) {
		return formattedAuthors[0];
	} else if (formattedAuthors.length === 2) {
		return `${formattedAuthors[0]}, & ${formattedAuthors[1]}`;
	} else {
		// For 3 or more authors (up to 20 per requirement), list ALL authors
		// Format: "Author1, F. M., Author2, F. M., & Author3, F. M."
		const allButLast = formattedAuthors.slice(0, -1).join(', ');
		const last = formattedAuthors[formattedAuthors.length - 1];
		return `${allButLast}, & ${last}`;
	}
};

/**
 * Format multiple authors for in-text citation
 * Rules:
 * - 1 author: "LastName"
 * - 2 authors: "LastName1 and LastName2" (narrative) or "LastName1 & LastName2" (parenthetical)
 * - 3+ authors: "LastName1 et al."
 * @param {Array} authors - Array of full names
 * @param {boolean} isNarrative - True for narrative citations (uses "and"), false for parenthetical (uses "&")
 * @returns {string} - Formatted authors string for in-text
 */
export const formatAuthorsForInText = (authors, isNarrative = false) => {
	if (!authors || authors.length === 0 || !authors[0]) return 'Author';

	const validAuthors = authors.filter((a) => a && a.trim());
	if (validAuthors.length === 0) return 'Author';

	if (validAuthors.length === 1) {
		return formatNameForInText(validAuthors[0]);
	} else if (validAuthors.length === 2) {
		// Use "and" for narrative citations, "&" for parenthetical
		const conjunction = isNarrative ? 'and' : '&';
		return `${formatNameForInText(validAuthors[0])} ${conjunction} ${formatNameForInText(validAuthors[1])}`;
	} else {
		return `${formatNameForInText(validAuthors[0])} et al.`;
	}
};


/**
 * Format multiple translators for reference citation
 * Rules:
 * - 1 translator: "F. M. LastName"
 * - 2 translators: "F. M. LastName1 & F. M. LastName2"
 * - 3+ translators: "F. M. LastName1 et al."
 * @param {Array} translators - Array of full names
 * @returns {string} - Formatted translators string
 */
export const formatTranslatorsForReference = (translators) => {
	if (!translators || translators.length === 0 || !translators[0]) return '';

	const validTranslators = translators.filter((t) => t && t.trim());
	if (validTranslators.length === 0) return '';

	const formattedTranslators = validTranslators.map((translator) =>
		formatTranslatorForReference(translator)
	);

	if (formattedTranslators.length === 1) {
		return formattedTranslators[0];
	} else if (formattedTranslators.length === 2) {
		return `${formattedTranslators[0]}, & ${formattedTranslators[1]}`;
	} else {
		// For 3+ translators, show first translator and "et al."
		return `${formattedTranslators[0]} et al.`;
	}
};

/**
 * Format multiple contributors for reference citation
 * Rules:
 * - 1 contributor: "LastName, F. M."
 * - 2 contributors: "LastName1, F. M., & LastName2, F. M."
 * - 3+ contributors: "LastName1, F. M., LastName2, F. M., & LastName3, F. M." (ALL listed, no et al.)
 * @param {Array} contributors - Array of full names
 * @returns {string} - Formatted contributors string (all contributors listed)
 */
export const formatContributorsForReference = (contributors) => {
	if (!contributors || contributors.length === 0 || !contributors[0]) return '';

	const validContributors = contributors.filter((c) => c && c.trim());
	if (validContributors.length === 0) return '';

	const formattedContributors = validContributors.map((contributor) =>
		formatNameForReference(contributor)
	);

	if (formattedContributors.length === 1) {
		return formattedContributors[0];
	} else if (formattedContributors.length === 2) {
		return `${formattedContributors[0]}, & ${formattedContributors[1]}`;
	} else {
		// For 3+ contributors, list ALL with commas and & before last one
		const allButLast = formattedContributors.slice(0, -1).join(', ');
		return `${allButLast}, & ${formattedContributors[formattedContributors.length - 1]}`;
	}
};

// Legacy helper - kept for backward compatibility, now uses new functions
export const formatAuthors = (authors) => {
	return formatAuthorsForReference(authors);
};

// Legacy helper - kept for backward compatibility
export const getFirstAuthorLastName = (authors) => {
	if (!authors || authors.length === 0 || !authors[0]) return 'Author';

	const validAuthors = authors.filter((a) => a && a.trim());
	if (validAuthors.length === 0) return 'Author';

	return formatNameForInText(validAuthors[0]);
};

// Helper to format year for in-text citation
export const extractYear = (dateOrYear) => {
	if (!dateOrYear) return 'n.d.';

	// If it's just a year
	if (/^\d{4}$/.test(dateOrYear.trim())) {
		return dateOrYear.trim();
	}

	// If it's a full date, extract the year (last 4 digits)
	const yearMatch = dateOrYear.match(/\d{4}/);
	return yearMatch ? yearMatch[0] : 'n.d.';
};

// Helper to format page range (replace hyphens with en-dashes)
export const formatPageRange = (range) => {
	if (!range) return '';
	return range.replace(/-/g, '–');
};

// Helper to format edition number to ordinal (e.g. "5" -> "5th")
export const formatEdition = (edition) => {
	if (!edition) return '';

	// If it's just a number, add ordinal suffix
	if (/^\d+$/.test(edition.trim())) {
		const num = parseInt(edition.trim(), 10);
		const j = num % 10;
		const k = num % 100;

		if (j === 1 && k !== 11) {
			return num + "st";
		}
		if (j === 2 && k !== 12) {
			return num + "nd";
		}
		if (j === 3 && k !== 13) {
			return num + "rd";
		}
		return num + "th";
	}

	// Otherwise return as is (e.g., "Revised", "3rd")
	return edition;
};

// Generate reference citation for different source types
export const generateReference = (category, sourceType, data) => {
	if (!category || !sourceType || !data) return '';

	try {
		// Web Content
		if (category === 'Web Content') {
			if (sourceType === 'Blog Post') {
				return generateBlogPost(data);
			} else if (sourceType === 'Webpage') {
				return generateWebpage(data);
			}
		}

		// Periodicals
		if (category === 'Periodicals') {
			if (sourceType === 'Journal Article') {
				return generateJournalArticle(data);
			} else if (sourceType === 'Magazine Article (Print & Online)') {
				return generateMagazineArticle(data);
			} else if (sourceType === 'Newspaper Article (Print & Online)') {
				return generateNewspaperArticle(data);
			} else if (sourceType === 'Preprint') {
				return generatePreprint(data);
			}
		}

		// Book & Chapter
		if (category === 'Book & Chapter') {
			if (sourceType === 'Book') {
				return generateBook(data);
			} else if (sourceType === 'Chapter in Edited Book') {
				return generateBookChapter(data);
			} else if (sourceType === 'Reference Work Entry') {
				return generateReferenceEntry(data);
			} else if (sourceType === 'Thesis/Dissertation') {
				return generateThesis(data);
			}
		}

		// Multimedia
		if (category === 'Multimedia') {
			if (sourceType === 'Film/Movie') {
				return generateFilm(data);
			} else if (sourceType === 'YouTube Video') {
				return generateYouTubeVideo(data);
			} else if (sourceType === 'TV Series Episode') {
				return generateTVEpisode(data);
			} else if (sourceType === 'Podcast Episode') {
				return generatePodcastEpisode(data);
			} else if (sourceType === 'Webinar/Lecture (recorded & retrievable)') {
				return generateWebinar(data);
			} else if (sourceType === 'Song/Track') {
				return generateSongTrack(data);
			} else if (sourceType === 'Infographic/Chart/Graph') {
				return generateInfographic(data);
			} else if (sourceType === 'Radio Broadcast') {
				return generateRadioBroadcast(data);
			}
		}

		// Social Media
		if (category === 'Social Media') {
			if (sourceType === 'X/Twitter Post') {
				return generateTwitterPost(data);
			}
		}

		// Personal Communication
		if (category === 'Personal Communication') {
			return 'Personal communications are cited only in-text and do not appear in the reference list.';
		}

		// Government & Institutional Publications
		if (category === 'Government & Institutional Publications') {
			if (sourceType === 'Press Release') {
				return generatePressRelease(data);
			} else if (sourceType === 'Institutional Report') {
				return generateInstitutionalReport(data);
			}
		}

		// Data & Software
		if (category === 'Data & Software') {
			if (sourceType === 'Data set') {
				return generateDataset(data);
			}
		}

		// Course & Education
		if (category === 'Course & Education') {
			if (sourceType === 'Course Handout(Education)') {
				return generateCourseHandout(data);
			} else if (sourceType === 'Conference Presentation') {
				return generateConferencePresentation(data);
			} else if (sourceType === 'Open Educational Resource') {
				return generateOER(data);
			} else if (sourceType === 'Conference Session') {
				return generateConferenceSession(data);
			}
		}

		return 'Fill in the fields to see your citation...';
	} catch (error) {
		return 'Fill in the required fields to generate citation...';
	}
};

// Generate in-text citation
export const generateInText = (category, sourceType, data) => {
	if (!category || !sourceType || !data) {
		return { parenthetical: '', narrative: '' };
	}

	// Personal Communication (special case)
	if (category === 'Personal Communication') {
		const author = data.authorCommunicator || 'Communicator';
		const date = data.date || 'Date';
		return {
			parenthetical: `(${author}, personal communication, ${date})`,
			narrative: `${author} (personal communication, ${date})`
		};
	}

	// Multimedia - Handle non-public items as personal communication
	if (category === 'Multimedia' && data.publiclyAccessible === 'No') {
		const authors =
			data.authorOrganization || data.authorUploader || data.artistGroup || data.contributors || [];
		const author = authors.length > 0 ? formatNameForInText(authors[0]) : 'Author';
		const date = data.date || 'Date';
		return {
			parenthetical: `(${author}, personal communication, ${date})`,
			narrative: `${author} (personal communication, ${date})`
		};
	}

	// Social Media - X/Twitter Post
	if (category === 'Social Media' && sourceType === 'X/Twitter Post') {
		// Handle Author(s) vs Organization
		let authorTextParenthetical = '';
		let authorTextNarrative = '';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			// Organization: format with abbreviation support (same for both)
			authorTextParenthetical = formatOrganizationForInText(data.organization, true);
			authorTextNarrative = formatOrganizationForInText(data.organization, true);
		} else {
			// Author(s): use the list with in-text formatting
			authorTextParenthetical = formatAuthorsForInText(data.author || [], false); // & for parenthetical
			authorTextNarrative = formatAuthorsForInText(data.author || [], true); // and for narrative
		}

		const year = data.year || data.date ? extractYear(data.year || data.date) : 'n.d.';

		// Note: Handle/username should NOT appear in in-text citations per APA 7th edition
		// It only appears in the full reference

		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Web Content - Webpage
	if (category === 'Web Content' && sourceType === 'Webpage') {
		// Handle Author(s) vs Organization
		let authorTextParenthetical = '';
		let authorTextNarrative = '';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			authorTextParenthetical = formatOrganizationForInText(data.organization, true);
			authorTextNarrative = formatOrganizationForInText(data.organization, true);
		} else {
			authorTextParenthetical = formatAuthorsForInText(data.author || [], false);
			authorTextNarrative = formatAuthorsForInText(data.author || [], true);
		}
		const year = data.year || data.date ? extractYear(data.year || data.date) : 'n.d.';

		// Note: Website name should NOT appear in in-text citations per APA 7th edition
		// It only appears in the full reference

		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Web Content - Blog Post
	if (category === 'Web Content' && sourceType === 'Blog Post') {
		// Handle Author(s) vs Organization
		let authorTextParenthetical = '';
		let authorTextNarrative = '';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			authorTextParenthetical = formatOrganizationForInText(data.organization, true);
			authorTextNarrative = formatOrganizationForInText(data.organization, true);
		} else {
			authorTextParenthetical = formatAuthorsForInText(data.author || [], false);
			authorTextNarrative = formatAuthorsForInText(data.author || [], true);
		}
		const year = data.year || data.date ? extractYear(data.year || data.date) : 'n.d.';

		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// TV Series Episode - Include both writer and director
	if (category === 'Multimedia' && sourceType === 'TV Series Episode') {
		const writers = data.writers || [];
		const directors = data.directors || [];
		const year = data.year || data.date ? extractYear(data.year || data.date) : 'n.d.';

		const writerText = formatAuthorsForInText(writers);
		const directorText = formatAuthorsForInText(directors);

		// Combine writer and director
		const authorText =
			writerText && directorText
				? `${writerText} & ${directorText}`
				: writerText || directorText || 'Author';

		return {
			parenthetical: `(${authorText}, ${year})`,
			narrative: `${authorText} (${year})`
		};
	}

	// Periodicals - Handle no author cases (Magazine, Newspaper, Preprint)
	if (category === 'Periodicals') {
		const year = data.year || data.date ? extractYear(data.year || data.date) : 'n.d.';

		// Check if author/organization is selected
		const hasAuthor =
			data.AuthorOrOrganization &&
			((data.AuthorOrOrganization === 'Author(s)' && data.author?.some((a) => a && a.trim())) ||
				(data.AuthorOrOrganization === 'Organization' && data.organization));

		// If no author, use shortened title
		if (!hasAuthor) {
			let title = '';
			if (sourceType === 'Magazine Article (Print & Online)') {
				title = data.titleOfArticle || 'Title';
			} else if (sourceType === 'Newspaper Article (Print & Online)') {
				title = data.titleOfArticle || 'Title';
			} else if (sourceType === 'Preprint') {
				title = data.titleOfManuscript || 'Title';
			}

			// Shorten title to first few words (max 4-5 words)
			const words = title.split(' ');
			const shortTitle = words.length > 4 ? words.slice(0, 4).join(' ') + '...' : title;

			// For articles use quotes, for preprint use italics
			const formattedTitle =
				sourceType === 'Preprint'
					? shortTitle // Will be italicized in the UI
					: `"${shortTitle}"`;

			return {
				parenthetical: `(${formattedTitle}, ${year})`,
				narrative: `${formattedTitle} (${year})`
			};
		}

		// If author exists, use standard format with new fields
		let authorTextParenthetical = '';
		let authorTextNarrative = '';
		if (data.AuthorOrOrganization === 'Organization' && data.organization) {
			authorTextParenthetical = formatOrganizationForInText(data.organization, true);
			authorTextNarrative = formatOrganizationForInText(data.organization, true);
		} else {
			authorTextParenthetical = formatAuthorsForInText(data.author || [], false);
			authorTextNarrative = formatAuthorsForInText(data.author || [], true);
		}
		return {
			parenthetical: `(${authorTextParenthetical}, ${year})`,
			narrative: `${authorTextNarrative} (${year})`
		};
	}

	// Get author and year for standard in-text citations
	// Handle new Author/Organization pattern first
	let authorTextParenthetical = '';
	let authorTextNarrative = '';
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		authorTextParenthetical = formatOrganizationForInText(data.organization, true);
		authorTextNarrative = formatOrganizationForInText(data.organization, true);
	} else if (data.AuthorOrOrganization === 'Author(s)' && data.author?.length > 0) {
		authorTextParenthetical = formatAuthorsForInText(data.author, false);
		authorTextNarrative = formatAuthorsForInText(data.author, true);
	} else {
		// Fallback to legacy field names
		const authors =
			data.authors ||
			data.chapterAuthors ||
			data.entryAuthors ||
			data.presentersOrganization ||
			data.presenterOrganization ||
			data.authorUploader ||
			data.writers ||
			data.contributors ||
			data.presentersLecturers ||
			data.contributorsOrganization ||
			data.artistGroup ||
			[];
		authorTextParenthetical = formatAuthorsForInText(authors, false);
		authorTextNarrative = formatAuthorsForInText(authors, true);
	}

	const year = data.year || data.date ? extractYear(data.year || data.date) : 'n.d.';
	const originalYear = data.originalWorkPublishedYear || '';

	// Format year with original year if provided (e.g., "2025/2000")
	const yearText = originalYear ? `${year}/${originalYear}` : year;

	return {
		parenthetical: `(${authorTextParenthetical}, ${yearText})`,
		narrative: `${authorTextNarrative} (${yearText})`
	};
};

// Specific generators for each source type

// Helper to get author text based on Author/Organization selection
function getAuthorText(data, fallback = 'Author') {
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		// For references, always show full name with abbreviation
		return formatOrganizationForInText(data.organization, true);
	}
	return formatAuthorsForReference(data.author || []) || fallback;
}

function generateBlogPost(data) {
	const authors = getAuthorText(data);
	const date = data.date || 'n.d.';
	const title = data.titleOfPost || 'Title of post';
	const blogName = data.blogName || 'Blog Name';
	const url = data.url || 'URL';

	return `${authors} (${date}). ${title}. <em>${blogName}</em>. ${url}`;
}

function generateWebpage(data) {
	const authors = getAuthorText(data);
	const date = data.date || 'n.d.';
	const title = data.titleOfWebPage || 'Title of web page';
	const siteName = data.websiteName ? ` ${data.websiteName}.` : '';
	const url = data.url || 'URL';
	const retrieval = data.retrievalDate ? `Retrieved ${data.retrievalDate}, from ` : '';

	return `${authors} (${date}). <em>${title}</em>.${siteName} ${retrieval}${url}`;
}

function generateJournalArticle(data) {
	const authors = getAuthorText(data);
	const year = data.year || 'n.d.';
	const title = data.titleOfArticle || 'Title of article';
	const journal = data.titleOfJournal || 'Journal Title';
	const volume = data.volume || 'Volume';
	const issue = data.issue ? `(${data.issue})` : '';
	const pages = data.pageRange ? formatPageRange(data.pageRange) : 'pp. XX–XX';
	const doi = data.doiUrl || '';

	return `${authors} (${year}). ${title}. <em>${journal}</em>, <em>${volume}</em>${issue}, ${pages}. ${doi}`;
}

function generateMagazineArticle(data) {
	// Check if author/organization is selected
	const hasAuthor =
		data.AuthorOrOrganization &&
		((data.AuthorOrOrganization === 'Author(s)' && data.author?.some((a) => a && a.trim())) ||
			(data.AuthorOrOrganization === 'Organization' && data.organization));
	const authors = hasAuthor ? getAuthorText(data, '') : '';
	const date = data.date || 'n.d.';
	const title = data.titleOfArticle || 'Title of article';
	const magazine = data.titleOfMagazine || 'Magazine Title';
	const volume = data.volume ? `, ${data.volume}` : '';
	const issue = data.issue ? `(${data.issue})` : '';
	const pages = data.pageRange ? `, ${formatPageRange(data.pageRange)}` : '';
	const doi = data.doiUrl || '';

	// If no author, start with title
	if (!authors) {
		return `${title}. (${date}). <em>${magazine}</em>, <em>${volume}</em>${issue}${pages}. ${doi}`;
	}

	return `${authors} (${date}). ${title}. <em>${magazine}</em>, <em>${volume}</em>${issue}${pages}. ${doi}`;
}

function generateNewspaperArticle(data) {
	// Check if author/organization is selected
	const hasAuthor =
		data.AuthorOrOrganization &&
		((data.AuthorOrOrganization === 'Author(s)' && data.author?.some((a) => a && a.trim())) ||
			(data.AuthorOrOrganization === 'Organization' && data.organization));
	const authors = hasAuthor ? getAuthorText(data, '') : '';
	const date = data.date || 'n.d.';
	const title = data.titleOfArticle || 'Title of article';
	const newspaper = data.titleOfNewspaper || 'Newspaper Title';
	const pages = data.pageNumbers ? `, ${formatPageRange(data.pageNumbers)}` : '';
	const url = data.doiUrl || '';

	// If no author, start with title
	if (!authors) {
		return `${title}. (${date}). <em>${newspaper}</em>${pages}. ${url}`;
	}

	return `${authors} (${date}). ${title}. <em>${newspaper}</em>${pages}. ${url}`;
}

function generatePreprint(data) {
	const authors = getAuthorText(data, '');
	const year = data.year || 'n.d.';
	const title = data.titleOfManuscript || 'Title of manuscript';
	const version = data.version ? ` (Version ${data.version})` : '';
	const descriptor = data.descriptor || 'Preprint';
	const repository = data.repositoryArchiveName || 'Repository Name';
	const doi = data.doiUrl || 'URL';

	// If no author, start with title
	if (!authors) {
		return `<em>${title}</em>${version}. (${year}). [${descriptor}]. <em>${repository}</em>. ${doi}`;
	}

	return `${authors} (${year}). <em>${title}</em>${version} [${descriptor}]. <em>${repository}</em>. ${doi}`;
}

function generateBook(data) {
	const authors = formatAuthorsForReference(data.authors || []);
	const translators = data.translators ? formatTranslatorsForReference(data.translators) : '';
	const year = data.year || 'n.d.';
	const title = data.titleOfBook || 'Title of book';
	const publisher = data.publisher || 'Publisher';
	const doi = data.doiUrl || '';
	const originalYear = data.originalWorkPublishedYear || '';

	// Build the parenthetical information (translator, edition, volume)
	const parentheticalParts = [];

	// Add translator
	if (translators) {
		parentheticalParts.push(`${translators}, Trans.`);
	}

	// Add edition (e.g., "3rd ed.")
	// Add edition (e.g., "3rd ed.")
	if (data.edition) {
		parentheticalParts.push(`${formatEdition(data.edition)} ed.`);
	}

	// Add volume (e.g., "Vols. 1-2" or "Vol. 1")
	if (data.volumes) {
		const volumeLabel = data.volumes.includes('-') || data.volumes.includes(',') ? 'Vols.' : 'Vol.';
		parentheticalParts.push(`${volumeLabel} ${data.volumes}`);
	}

	const parentheticalText =
		parentheticalParts.length > 0 ? ` (${parentheticalParts.join('; ')})` : '';

	// Add original work published year at the end
	const originalYearText = originalYear ? ` (Original work published ${originalYear})` : '';

	return `${authors || 'Author'
		} (${year}). <em>${title}</em>${parentheticalText}. ${publisher}. ${doi}${originalYearText}`;
}

function generateBookChapter(data) {
	const authors = formatAuthorsForReference(data.chapterAuthors || []);
	const year = data.year || 'n.d.';
	const chapterTitle = data.titleOfChapter || 'Title of chapter';
	const editors = formatTranslatorsForReference(data.editors || []);
	const translators = data.translators ? formatTranslatorsForReference(data.translators) : '';
	const editorLabel = data.editors && data.editors.length > 1 ? 'Eds.' : 'Ed.';
	const bookTitle = data.titleOfBook || 'Title of book';
	// const pages = data.pageRange || "pp. XX–XX"
	const publisher = data.publisher || 'Publisher';
	const doi = data.doiUrl || '';
	const originalYear = data.originalWorkPublishedYear || '';

	// Build the book parenthetical information (translator, edition, volume, page range)
	const bookParentheticalParts = [];

	// Add translator
	if (translators) {
		bookParentheticalParts.push(`${translators}, Trans.`);
	}

	// Add edition
	if (data.edition) {
		bookParentheticalParts.push(`${formatEdition(data.edition)} ed.`);
	}

	// Add volume
	if (data.volumes) {
		const volumeLabel = data.volumes.includes('-') || data.volumes.includes(',') ? 'Vols.' : 'Vol.';
		bookParentheticalParts.push(`${volumeLabel} ${data.volumes}`);
	}

	// Add page range
	if (data.pageRange) {
		bookParentheticalParts.push(`pp. ${formatPageRange(data.pageRange)}`);
	}

	const bookParentheticalText =
		bookParentheticalParts.length > 0 ? ` (${bookParentheticalParts.join('; ')})` : '';

	const originalYearText = originalYear ? ` (Original work published ${originalYear})` : '';

	return `${authors || 'Author'} (${year}). ${chapterTitle}. In ${editors || 'Editor'
		} (${editorLabel}), <em>${bookTitle}</em>${bookParentheticalText}. ${publisher}. ${doi}${originalYearText}`;
}

function generateReferenceEntry(data) {
	const authors = formatAuthorsForReference(data.entryAuthors || []);
	const year = data.year || 'n.d.';
	const title = data.titleOfEntry || 'Title of entry';
	const editors = formatTranslatorsForReference(data.referenceWorkEditors || []);
	const translators = data.translators ? formatTranslatorsForReference(data.translators) : '';
	const refWork = data.titleOfReferenceWork || 'Reference Work Title';
	const publisher = data.publisher || 'Publisher';
	const url = data.doiUrl || '';
	const retrieval = data.retrievalDate ? `Retrieved ${data.retrievalDate}, from ` : '';
	const originalYear = data.originalWorkPublishedYear || '';
	const entryEditors = data.entryEditors ? formatTranslatorsForReference(data.entryEditors) : '';
	const entryEditorsText = entryEditors ? ` [Revised by ${entryEditors}]` : '';

	// Build the reference work parenthetical information (translator, edition, volume, page range)
	const refWorkParentheticalParts = [];

	// Add translator
	if (translators) {
		refWorkParentheticalParts.push(`${translators}, Trans.`);
	}

	// Add edition
	if (data.edition) {
		refWorkParentheticalParts.push(`${formatEdition(data.edition)} ed.`);
	}

	// Add volume
	if (data.volumes) {
		const volumeLabel = data.volumes.includes('-') || data.volumes.includes(',') ? 'Vols.' : 'Vol.';
		refWorkParentheticalParts.push(`${volumeLabel} ${data.volumes}`);
	}

	// Add page range
	if (data.pageRange) {
		refWorkParentheticalParts.push(`pp. ${formatPageRange(data.pageRange)}`);
	}

	const refWorkParentheticalText =
		refWorkParentheticalParts.length > 0 ? ` (${refWorkParentheticalParts.join('; ')})` : '';

	const originalYearText = originalYear ? ` (Original work published ${originalYear})` : '';

	if (editors) {
		const editorLabel =
			data.referenceWorkEditors && data.referenceWorkEditors.length > 1 ? 'Eds.' : 'Ed.';
		return `${authors || 'Author'
			} (${year}). ${title}${entryEditorsText}. In ${editors} (${editorLabel}), <em>${refWork}</em>${refWorkParentheticalText}. ${publisher}. ${retrieval}${url}${originalYearText}`;
	}

	return `${authors || 'Author'
		} (${year}). ${title}${entryEditorsText}. In <em>${refWork}</em>${refWorkParentheticalText}. ${publisher}. ${retrieval}${url}${originalYearText}`;
}

function generateThesis(data) {
	const authors = formatAuthorsForReference(data.authors || []);
	const year = data.year || 'n.d.';
	const title = data.titleOfThesisDissertation || 'Title';
	const publicationNumber = data.publicationNumberAccessionId || '';
	const publicationNumberText = publicationNumber ? ` (Publication No. ${publicationNumber})` : '';
	const descriptor = data.descriptor || 'Doctoral dissertation';
	const institution = data.institution || 'Institution';
	const repository = data.publisherRepository || 'Repository';
	const doi = data.doiUrl || '';

	return `${authors || 'Author'
		} (${year}). <em>${title}</em>${publicationNumberText} [${descriptor}, ${institution}]. ${repository}. ${doi}`;
}

function generateFilm(data) {
	const contributorsList = data.contributors || [];
	const roles = data.contributorRoles || [];
	const date = data.date || 'Year';
	const title = data.titleOfWork || 'Title of film';
	const descriptor = data.descriptor || 'Film';
	const company = data.productionCompany || '';
	const platform = data.platform ? ` ${data.platform}.` : '';
	const url = data.url || '';

	// Format contributors with their roles
	let contributorsText = '';
	const validContributors = contributorsList.filter((c) => c && c.trim());

	if (validContributors.length === 0) {
		contributorsText = 'Director (Director)';
	} else {
		// Format all contributor names with roles
		const formattedNames = validContributors.map((contributor, index) => {
			const name = formatNameForReference(contributor);
			const role = roles[index] || 'Role';
			// If role is "Organization (no role)", don't include the role in parentheses
			if (role === 'Organization (no role)') {
				return name;
			}
			return `${name} (${role})`;
		});

		// Join contributors with proper formatting
		if (formattedNames.length === 1) {
			contributorsText = formattedNames[0];
		} else if (formattedNames.length === 2) {
			contributorsText = `${formattedNames[0]} & ${formattedNames[1]}`;
		} else {
			// 3+ contributors: list all with commas and & before last
			const allButLast = formattedNames.slice(0, -1).join(', ');
			contributorsText = `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
		}
	}

	return `${contributorsText}. (${date}). <em>${title}</em> [${descriptor}]. ${company}.${platform} ${url}`;
}

function generateYouTubeVideo(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	const author = formatAuthorsForReference(data.authorUploader || []);
	const username = data.handleUsername ? ` [@${data.handleUsername}]` : '';
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfVideo || 'Title of video';
	const descriptor = data.descriptor || 'Video';
	const platform = data.platformName || 'YouTube';
	const url = data.url || 'URL';

	return `${author || 'Author'
		}${username} (${date}). ${title} [${descriptor}]. ${platform}. ${url}`;
}

function generateTVEpisode(data) {
	const writers = formatAuthorsForReference(data.writers || []);
	const directors = formatAuthorsForReference(data.directors || []);
	const executiveProducers = formatAuthorsForReference(data.executiveProducers || []);
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfEpisode || 'Title of episode';
	const season = data.season ? `Season ${data.season}` : '';
	const episode = data.episodeNumber ? `, Episode ${data.episodeNumber}` : '';
	const descriptor = data.descriptor || 'TV series episode';
	const series = data.titleOfSeries || 'Series Title';
	const company = data.productionCompany || 'Production Company';
	const url = data.url || '';
	const execProducerText = executiveProducers ? `${executiveProducers} (Executive Producer), ` : '';

	return `${writers || 'Writer'} (Writer), & ${directors || 'Director'
		} (Director) (${date}). ${title} (${season}${episode}) [${descriptor}]. In ${execProducerText}<em>${series}</em>. ${company}. ${url}`;
}

function generatePodcastEpisode(data) {
	const contributorsList = data.contributors || [];
	const roles = data.contributorRoles || [];
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfEpisode || 'Title of episode';
	const episodeNum = data.episodeNumber ? ` (No. ${data.episodeNumber})` : '';
	const descriptor = data.descriptor || 'Select Descriptor';
	const podcast = data.titleOfPodcast || 'Podcast Title';
	const publisher = data.publisherNetwork ? ` ${data.publisherNetwork}.` : '';
	const url = data.url || 'URL';

	// Format contributors with their roles
	let contributorsText = '';
	const validContributors = contributorsList.filter((c) => c && c.trim());

	if (validContributors.length === 0) {
		contributorsText = 'Host (Host)';
	} else {
		// Format all contributor names with roles
		const formattedNames = validContributors.map((contributor, index) => {
			const name = formatNameForReference(contributor);
			const role = roles[index] || 'Host';
			// If role is "Organization (no role assigned)", don't include the role in parentheses
			if (role === 'Organization (no role assigned)') {
				return name;
			}
			return `${name} (${role})`;
		});

		// Join contributors with proper formatting
		if (formattedNames.length === 1) {
			contributorsText = formattedNames[0];
		} else if (formattedNames.length === 2) {
			contributorsText = `${formattedNames[0]} & ${formattedNames[1]}`;
		} else {
			// 3+ contributors: list all with commas and & before last
			const allButLast = formattedNames.slice(0, -1).join(', ');
			contributorsText = `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
		}
	}

	return `${contributorsText}. (${date}). ${title}${episodeNum} [${descriptor}]. In <em>${podcast}</em>.${publisher} ${url}`;
}

function generateWebinar(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	const presentersList = data.presentersLecturers || [];
	const roles = data.presenterRoles || [];
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfRecording || 'Title';
	const descriptor = data.descriptor || 'Webinar';
	const platform = data.platform || 'Platform';
	const url = data.doiUrl || 'URL';

	// Format presenters with their roles
	let presentersText = '';
	const validPresenters = presentersList.filter((p) => p && p.trim());

	if (validPresenters.length === 0) {
		presentersText = 'Presenter';
	} else if (roles && roles.length > 0 && roles.some((r) => r && r.trim())) {
		// Format presenters with roles if roles are provided
		const formattedNames = validPresenters.map((presenter, index) => {
			const name = formatNameForReference(presenter);
			const role = roles[index];
			// Only add role if it exists and is not empty
			if (role && role.trim()) {
				return `${name} (${role})`;
			}
			return name;
		});

		// Join presenters with proper formatting
		if (formattedNames.length === 1) {
			presentersText = formattedNames[0];
		} else if (formattedNames.length === 2) {
			presentersText = `${formattedNames[0]} & ${formattedNames[1]}`;
		} else {
			// 3+ presenters: list all with commas and & before last
			const allButLast = formattedNames.slice(0, -1).join(', ');
			presentersText = `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
		}
	} else {
		// No roles provided, just format names
		const formattedNames = validPresenters.map((presenter) => formatNameForReference(presenter));

		if (formattedNames.length === 1) {
			presentersText = formattedNames[0];
		} else if (formattedNames.length === 2) {
			presentersText = `${formattedNames[0]} & ${formattedNames[1]}`;
		} else {
			const allButLast = formattedNames.slice(0, -1).join(', ');
			presentersText = `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
		}
	}

	return `${presentersText}. (${date}). <em>${title}</em> [${descriptor}]. ${platform}. ${url}`;
}

function generateSongTrack(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	const artists = formatAuthorsForReference(data.artistGroup || []);
	const year = data.year || 'n.d.';
	const title = data.titleOfSong || 'Title of song';
	const descriptor = data.descriptor || 'Song';
	const album = data.titleOfAlbum ? ` On <em>${data.titleOfAlbum}</em>.` : '';
	const label = data.recordLabel ? ` ${data.recordLabel}.` : '';
	const platform = data.platform ? ` ${data.platform}.` : '';
	const url = data.url ? ` ${data.url}` : '';

	return `${artists || 'Artist'
		} (${year}). ${title} [${descriptor}].<em>${album}</em>${label}${platform}${url}`;
}

function generateInfographic(data) {
	const authors = getAuthorText(data);
	const date = data.date || 'Year';
	const title = data.titleOfWork || 'Title of work';
	const descriptor = data.descriptor || 'Descriptor';
	const publisher = data.platformPublisher ? ` ${data.platformPublisher}.` : '';
	const retrieval = data.retrievalDate ? `Retrieved ${data.retrievalDate}, from ` : '';
	const url = data.doiUrl || 'URL';

	return `${authors} (${date}). <em>${title}</em> [${descriptor}].${publisher} ${retrieval}${url}`;
}

function generateRadioBroadcast(data) {
	const contributorsList = data.contributorsOrganization || [];
	const roles = data.contributorRoles || [];
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfEpisode || 'Title of episode';
	const episodeNum = data.episodeNumber ? ` (No. ${data.episodeNumber})` : '';
	const descriptor = data.descriptor || 'Radio broadcast';
	const series = data.titleOfSeries || 'Radio Series';
	const company = data.productionCompany || 'Production Company';
	const url = data.url ? ` ${data.url}` : '';

	// Format contributors with their roles
	let contributorsText = '';
	const validContributors = contributorsList.filter((c) => c && c.trim());

	if (validContributors.length === 0) {
		contributorsText = 'Host (Host)';
	} else {
		// Format all contributor names with roles
		const formattedNames = validContributors.map((contributor, index) => {
			const name = formatNameForReference(contributor);
			const role = roles[index] || 'Role';
			return `${name} (${role})`;
		});

		// Join contributors with proper formatting
		if (formattedNames.length === 1) {
			contributorsText = formattedNames[0];
		} else if (formattedNames.length === 2) {
			contributorsText = `${formattedNames[0]} & ${formattedNames[1]}`;
		} else {
			// 3+ contributors: list all with commas and & before last
			const allButLast = formattedNames.slice(0, -1).join(', ');
			contributorsText = `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
		}
	}

	return `${contributorsText}. (${date}). <em>${title}</em>${episodeNum} [${descriptor}]. In <em>${series}</em>. ${company}.${url}`;
}

function generateTwitterPost(data) {
	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	// Handle Author(s) vs Organization
	let authorText = '';
	if (data.AuthorOrOrganization === 'Organization' && data.organization) {
		// Organization: use the text directly (no name formatting)
		authorText = data.organization;
	} else {
		// Author(s): use the list with formatting
		authorText = formatAuthorsForReference(data.author || []) || 'Author';
	}

	const username = data.handleUsername ? ` [@${data.handleUsername}]` : '';
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfPost || 'Title of post';
	const descriptor = data.descriptor || 'Post';
	const platform = data.platform || 'X';
	const url = data.url || 'URL';
	const retrieval = data.retrievalDate ? `Retrieved ${data.retrievalDate}, from ` : '';

	return `${authorText}${username} (${date}). ${title} [${descriptor}]. ${platform}. ${retrieval}${url}`;
}

function generatePressRelease(data) {
	const authors = getAuthorText(data, 'Organization');
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfPressRelease || 'Title of press release';
	const descriptor = data.descriptor || 'Press release';
	const url = data.url || 'URL';
	const websitePublisher = data.websitePublisher || '';

	return `${authors} (${date}). <em>${title}</em> [${descriptor}]. ${websitePublisher}. ${url}`;
}

function generateInstitutionalReport(data) {
	const authors = getAuthorText(data, 'Organization');
	const year = data.year || 'Year';
	const title = data.titleOfReport || 'Title of report';
	const reportNum = data.reportNumber ? ` (Report No. ${data.reportNumber})` : '';
	const publisher = data.publisher ? ` ${data.publisher}.` : '';
	const url = data.doiUrl || '';

	return `${authors} (${year}). <em>${title}</em>${reportNum}.${publisher} ${url}`;
}

function generateDataset(data) {
	const authors = getAuthorText(data);
	const year = data.year || 'Year';
	const title = data.titleOfDataset || 'Title of dataset';
	const version = data.version ? ` (${data.version})` : '';
	const descriptor = data.descriptor || 'Data set';
	const publisher = data.publisher || data.platform || 'Publisher';
	const url = data.doiUrl || 'URL';
	const retrievalDate = data.retrievalDate || '';
	const retrievalText = retrievalDate ? `Retrieved ${retrievalDate}, from ` : '';

	return `${authors} (${year}). ${title}${version} [${descriptor}]. ${publisher}. ${retrievalText}${url}`;
}

function generateCourseHandout(data) {
	const presenter = formatAuthorsForReference(data.presenterOrganization || []);
	const date = data.date || 'Year';
	const title = data.titleOfPresentation || 'Title';
	const format = data.format || 'Course handout';
	const dept = data.department ? `${data.department}, ` : '';
	const institution = data.institution || 'Institution';
	const url = data.url || '';
	const course = data.course ? `${data.course}, ` : '';

	return `${presenter || 'Instructor'
		} (${date}). ${title} [${format}]. ${course}${dept}${institution}. ${url}`;
}

function generateConferencePresentation(data) {
	const presenters = formatAuthorsForReference(data.presentersOrganization || []);
	const date = data.date || 'Year, Month';
	const title = data.titleOfPaper || 'Title of presentation';
	const descriptor = data.descriptor || 'Conference presentation';
	const conference = data.conferenceDetails || 'Conference Name, Location';
	const url = data.doiUrl || '';

	return `${presenters || 'Presenter'} (${date}). ${title} [${descriptor}]. ${conference}. ${url}`;
}

function generateOER(data) {
	const authors = getAuthorText(data);
	const year = data.year || 'Year';
	const title = data.titleOfOer || 'Title of OER';
	const version = data.versionEdition ? ` (${formatEdition(data.versionEdition)} ed.)` : '';
	const descriptor = data.descriptor ? ` [${data.descriptor}]` : '';
	const license = data.license ? ` [${data.license}]` : '';
	const publisher = data.platformPublisher || 'Publisher';
	const url = data.url || 'URL';
	const retrievalDate = data.retrievalDate || '';
	const retrievalText = retrievalDate ? `Retrieved ${retrievalDate}, from ` : '';

	return `${authors || 'Author'
		} (${year}). <em>${title}</em>${version}${descriptor}${license}. ${publisher}. ${retrievalText}${url}`;
}

function generateConferenceSession(data) {
	const contributorsList = data.contributorsOrganization || [];
	const roles = data.contributorRoles || [];
	const date = data.date || 'Year, Month Day';
	const title = data.titleOfPresentation || 'Title of session';
	const descriptor = data.descriptor || 'Conference session';
	const conference = data.nameOfConference || 'Conference Name';
	const location = data.locationOfConference || 'Location';
	const url = data.url || '';
	const urlText = url ? ` ${url}` : '';

	// Check if publicly accessible
	if (data.publiclyAccessible === 'No') {
		return 'Personal communications are cited only in-text and do not appear in the reference list.';
	}

	// Format contributors with their roles
	let contributorsText = '';
	const validContributors = contributorsList.filter((c) => c && c.trim());

	if (validContributors.length === 0) {
		contributorsText = 'Chair (Chair)';
	} else {
		// Format all contributor names
		const formattedNames = validContributors.map((contributor, index) => {
			const name = formatNameForReference(contributor);
			const role = roles[index] || 'Role';
			// If role is "Organization", don't include the role in parentheses
			if (role === 'Organization') {
				return name;
			}
			return `${name} (${role})`;
		});

		// Join contributors with proper formatting
		if (formattedNames.length === 1) {
			contributorsText = formattedNames[0];
		} else if (formattedNames.length === 2) {
			contributorsText = `${formattedNames[0]} & ${formattedNames[1]}`;
		} else {
			// 3+ contributors: list all with commas and & before last
			const allButLast = formattedNames.slice(0, -1).join(', ');
			contributorsText = `${allButLast}, & ${formattedNames[formattedNames.length - 1]}`;
		}
	}

	return `${contributorsText} (${date}). <em>${title}</em> [${descriptor}]. <em>${conference}</em>, ${location}.${urlText}`;
}
